import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, TableSkeleton } from "@/components/shared";
import { money, fmtDate, label } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, TrendingUp, Loader2 } from "lucide-react";

const REGIONS = ["US", "CA", "GCC", "IN"];
const RCCY = { US: "USD", CA: "CAD", GCC: "AED", IN: "INR" };
const EMPTY = { company: "", contact_name: "", industry: "", region: "US", currency: "USD", service_requirement: "", source: "", stage: "new_lead", value: 0, probability: 10, expected_close: "", competitor: "", notes: "" };

export default function SalesPipeline() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sales/leads", { params: { page_size: 200 } }); setData(data); setStages(data.stages || []); }
    catch (e) { toast.error(apiErr(e)); setData({ items: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setF({ ...EMPTY, ...l, expected_close: l.expected_close || "" }); setOpen(true); };

  const save = async () => {
    if (!f.company.trim()) { toast.error("Company required"); return; }
    setBusy(true);
    const payload = { ...f, value: Number(f.value) || 0, probability: Number(f.probability) || 0 };
    try {
      if (editing) await api.put(`/sales/leads/${editing.id}`, payload);
      else await api.post("/sales/leads", payload);
      toast.success(editing ? "Lead updated" : "Lead created"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const changeStage = async (l, stage) => {
    if (stage === "lost") { const reason = window.prompt("Loss reason (required):"); if (!reason) return; try { await api.put(`/sales/leads/${l.id}/stage`, { stage, loss_reason: reason }); toast.success("Updated"); load(); } catch (e) { toast.error(apiErr(e)); } return; }
    try { await api.put(`/sales/leads/${l.id}/stage`, { stage }); toast.success(`Moved to ${label(stage)}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const convert = async (l) => {
    try { await api.post(`/sales/leads/${l.id}/convert`); toast.success("Converted to client"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Sales Pipeline" subtitle={data ? `${data.total} leads & opportunities` : "BDM leads and opportunities"}
        actions={can("sales:manage") && <Button size="sm" onClick={openNew} data-testid="lead-create-button" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Plus className="h-4 w-4" /> New Lead</Button>} />
      <Card className="overflow-hidden">
        {!data ? <div className="p-4"><TableSkeleton /></div>
          : data.items.length === 0 ? <EmptyState icon={TrendingUp} title="No leads yet" description="Create a lead to start your sales pipeline." />
            : (<div className="overflow-x-auto scrollbar-thin"><Table>
                <TableHeader><TableRow><TableHead>Company</TableHead><TableHead className="hidden md:table-cell">Value</TableHead><TableHead className="hidden lg:table-cell">Prob.</TableHead><TableHead>Stage</TableHead><TableHead className="hidden lg:table-cell">Owner</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{data.items.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-accent/40">
                    <TableCell className="cursor-pointer" onClick={() => openEdit(l)}><div className="font-medium">{l.company}</div><div className="text-xs text-muted-foreground">{l.industry} · {l.region}</div></TableCell>
                    <TableCell className="hidden md:table-cell">{money(l.value, l.currency)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{l.probability}%</TableCell>
                    <TableCell>{can("sales:manage") ? (
                      <Select value={l.stage} onValueChange={(v) => changeStage(l, v)}><SelectTrigger className="h-8 w-44" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger><SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent></Select>
                    ) : <StatusBadge value={l.stage} />}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{l.owner_name}</TableCell>
                    <TableCell className="text-right">{can("sales:manage") && !l.converted_client_id && ["qualified", "negotiation", "agreement_signed", "won"].includes(l.stage) && <Button size="sm" variant="outline" onClick={() => convert(l)} data-testid={`lead-convert-${l.id}`}>Convert</Button>}{l.converted_client_id && <StatusBadge value="won" />}</TableCell>
                  </TableRow>))}</TableBody></Table></div>)}
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin" data-testid="lead-form-sheet">
          <SheetHeader><SheetTitle className="font-display">{editing ? "Edit Lead" : "New Lead"}</SheetTitle><SheetDescription>Capture lead details, value and pipeline stage.</SheetDescription></SheetHeader>
          <div className="space-y-4 mt-6">
            <Fld label="Company *"><Input data-testid="lf-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></Fld>
            <div className="grid grid-cols-2 gap-4">
              <Fld label="Contact"><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></Fld>
              <Fld label="Industry"><Input value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} /></Fld>
              <Fld label="Region"><Select value={f.region} onValueChange={(v) => setF({ ...f, region: v, currency: RCCY[v] })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></Fld>
              <Fld label="Source"><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Fld>
              <Fld label="Est. Value"><Input type="number" data-testid="lf-value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></Fld>
              <Fld label="Probability %"><Input type="number" value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} /></Fld>
              <Fld label="Expected Close"><Input type="date" value={f.expected_close ? String(f.expected_close).slice(0, 10) : ""} onChange={(e) => setF({ ...f, expected_close: e.target.value })} /></Fld>
              <Fld label="Competitor"><Input value={f.competitor} onChange={(e) => setF({ ...f, competitor: e.target.value })} /></Fld>
            </div>
            <Fld label="Service Requirement"><Input value={f.service_requirement} onChange={(e) => setF({ ...f, service_requirement: e.target.value })} /></Fld>
            <Fld label="Notes"><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Fld>
            <Button className="w-full bg-[#255058] hover:bg-[#1c3d43] text-white" onClick={save} disabled={busy} data-testid="lf-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save" : "Create Lead")}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
function Fld({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
